import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, startWith } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideChevronRight,
  lucideClock,
  lucideHistory,
  lucideInbox,
  lucidePlug,
  lucideSettings2,
  lucideWorkflow
} from '@ng-icons/lucide';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';
import { NavUserComponent } from '@shared/components/nav-user/nav-user.component';
import { NotificationsComponent } from '@shared/components/notifications/notifications.component';
import { BillingService } from '@core/data/service/billing.service';
import { InboxService } from '@core/data/service/inbox.service';
import { UserService } from '@core/data/service/user.service';
import { LANDING_PATH } from '@core/services/auth/landing-path';
import { HlmToasterImports } from '@spartan-ng/helm/sonner';
import { HlmSidebarImports } from '@spartan-ng/helm/sidebar';
import { HlmCollapsibleImports } from '@spartan-ng/helm/collapsible';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  imports: [
    RouterModule,
    NgIcon,
    PageHeaderComponent,
    NavUserComponent,
    NotificationsComponent,
    HlmToasterImports,
    HlmSidebarImports,
    HlmCollapsibleImports,
    HlmSeparatorImports
  ],
  providers: [provideIcons({
    lucideChevronRight,
    lucideClock,
    lucideHistory,
    lucideInbox,
    lucidePlug,
    lucideSettings2,
    lucideWorkflow
  })]
})
export class AppComponent {
  private userService = inject(UserService);
  private router = inject(Router);
  private inboxService = inject(InboxService);
  protected billingService = inject(BillingService);

  private readonly mainItemsConfig: SideMenuItem[] = [
    { id: 'workflows', title: 'Workflows', icon: 'lucideWorkflow', link: '/workflows', hasAuthority: 'VIEW_PAGE_WORKFLOWS' },
    { id: 'runs', title: 'Runs', icon: 'lucideHistory', link: '/runs', hasAuthority: 'VIEW_PAGE_WORKFLOWS' },
    { id: 'inbox', title: 'Inbox', icon: 'lucideInbox', link: '/inbox', hasAuthority: 'VIEW_PAGE_WORKFLOWS' },
    { id: 'connections', title: 'Connections', icon: 'lucidePlug', link: '/connections', hasAuthority: 'MANAGE_CONNECTIONS' }
  ];

  private readonly settingsItemsConfig: SideMenuItem[] = [
    { id: 'teams', title: 'Teams', link: '/settings/team-management/teams', hasAuthority: 'VIEW_PAGE_TEAM_MANAGEMENT' },
    { id: 'users', title: 'Users', link: '/settings/user-management/users', hasAuthority: 'VIEW_PAGE_USER_MANAGEMENT' },
    { id: 'ai-policies', title: 'AI policies', link: '/settings/ai-policies', hasAuthority: 'MANAGE_AI_SETTINGS' }
  ];

  readonly mainItems = computed(() => this.allowed(this.mainItemsConfig));
  readonly settingsItems = computed(() => this.allowed(this.settingsItemsConfig));

  /** Pending approvals and tasks for the caller; the badge hides at zero. */
  readonly inboxCount = computed(() => {
    const counts = this.inboxService.counts();
    return counts ? counts.approvals + counts.tasks : 0;
  });

  readonly settingsExpanded = signal(false);

  readonly landingPath = LANDING_PATH;
  readonly organizationName = computed(() => this.userService.currentUser?.organization?.name ?? '');

  private readonly url = signal(this.router.url);
  readonly isPlansPage = computed(() => this.url().startsWith('/plans'));
  /** A page whose route sets `data: { fullBleed: true }` fills the content area without the page padding. */
  readonly fullBleed = signal(false);

  readonly trialLabel = computed(() => {
    const days = this.billingService.activeSubscription?.trialDaysRemaining ?? 0;
    return `Trial · ${ days } ${ days === 1 ? 'day' : 'days' } left`;
  });

  constructor() {
    // The sidebar shows once the subscription is known to give access.
    effect(() => {
      if (this.billingService.activeSubscription?.hasAppAccess && this.mainItems().some(item => item.id === 'inbox')) {
        this.inboxService.watchCounts();
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.url.set(this.router.url);
      let route: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
      while (route.firstChild) {
        route = route.firstChild;
      }
      this.fullBleed.set(route.data['fullBleed'] === true);
      // Opens the group on a settings page; leaving one keeps whatever the user chose.
      if (this.settingsItems().some(item => this.router.url.startsWith(item.link))) {
        this.settingsExpanded.set(true);
      }
    });
  }

  private allowed(items: SideMenuItem[]): SideMenuItem[] {
    const authorities = this.userService.currentUser?.authorities || [];
    return items.filter(item => !item.hasAuthority || authorities.includes(item.hasAuthority));
  }
}

interface SideMenuItem {
  id: string;
  title: string;
  icon?: string;
  link: string;
  hasAuthority?: string;
}
